import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Rss, Plus, Trash2, Store } from 'lucide-react';
import { useSelection } from '@/contexts/SelectionContext';
import { EmptyState } from '@/components/EmptyState';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FeedCardSkeleton } from '@/components/skeletons/FeedCardSkeleton';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SupplementalFeedInline } from '@/components/products/SupplementalFeedInline';
import CustomInstructions from '@/components/feeds/CustomInstructions';
import { PageHeader } from '@/components/PageHeader';

interface Feed {
  id: string;
  feed_id: string;
  display_name: string;
  target_country: string;
  content_language: string;
  merchant_center_id: string;
  product_count?: number;
  custom_prompt_instructions?: string | null;
}

interface SupplementalFeed {
  id: string;
  supplemental_feed_id: string;
  display_name: string;
  status: string;
  feed_id: string;
  last_upload_at: string | null;
  products_uploaded: number;
  created_at: string;
}

const Feeds: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { selectedMerchantCenterId, setSelectedFeed, selectedFeedId, merchantCenter } = useSelection();
  const [deletingSupplementalFeed, setDeletingSupplementalFeed] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedToDelete, setFeedToDelete] = useState<SupplementalFeed | null>(null);
  const [creatingForFeedId, setCreatingForFeedId] = useState<string | null>(null);
  const [deletingForFeedId, setDeletingForFeedId] = useState<string | null>(null);

  const { data: feeds, isLoading: loadingFeeds } = useQuery({
    queryKey: ['feeds', selectedMerchantCenterId],
    queryFn: async () => {
      if (!selectedMerchantCenterId) return [];
      
      const { data, error } = await supabase
        .from('feeds')
        .select('*, products(count)')
        .eq('merchant_center_id', selectedMerchantCenterId)
        .order('display_name', { ascending: true }) as any;

      if (error) throw error;
      
      // Transform to include product count
      const feedsWithCounts = data?.map(feed => ({
        ...feed,
        product_count: feed.products?.[0]?.count || 0
      })) || [];
      
      return feedsWithCounts as Feed[];
    },
    enabled: !!selectedMerchantCenterId,
  });

  const { data: supplementalFeeds, isLoading: loadingSupplementalFeeds, refetch: refetchSupplementalFeeds } = useQuery({
    queryKey: ['supplemental-feeds', selectedMerchantCenterId],
    queryFn: async () => {
      if (!selectedMerchantCenterId) return [];
      
      // Get all feeds for this merchant center first
      const { data: feedsData } = await supabase
        .from('feeds')
        .select('id')
        .eq('merchant_center_id', selectedMerchantCenterId);
      
      if (!feedsData || feedsData.length === 0) return [];
      
      const feedIds = feedsData.map(f => f.id);
      
      // Get supplemental feeds for all these feeds
      const { data, error } = await supabase
        .from('supplemental_feeds')
        .select('*')
        .in('feed_id', feedIds);

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return (data || []) as SupplementalFeed[];
    },
    enabled: !!selectedMerchantCenterId,
  });

  const handleSelectFeed = async (feedId: string) => {
    await setSelectedFeed(feedId);
    toast.success(t('feeds.feedSelected'), {
      description: t('feeds.feedSelectedDesc'),
    });
  };

  const handleCreateSupplementalFeed = async (feedId: string) => {
    if (!feedId || !selectedMerchantCenterId) {
      toast.error(t('feeds.selectFeedFirst'), {
        description: t('feeds.selectFeedFirstDesc'),
      });
      return;
    }

    setCreatingForFeedId(feedId);
    try {
      const res = await supabase.functions.invoke('create-supplemental-feed', {
        body: {
          feed_id: feedId,
          merchant_center_id: selectedMerchantCenterId,
        }
      });

      // Check for token expiration in response data first (even with error status)
      if (res.data?.code === 'GOOGLE_REAUTH_REQUIRED') {
        sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
        toast.error(t('feeds.reconnectionRequired'), {
          description: (
            <span>
              {t('feeds.reconnectionRequiredDesc')}{' '}
              <a href="/settings?tab=accounts" className="text-primary underline">{t('googleAccounts.title')}</a>.
            </span>
          ),
        });
        setTimeout(() => navigate('/settings?tab=accounts'), 1500);
        return;
      }

      if (res.error) throw res.error;

      toast.success(t('feeds.supplementalFeedCreated'), {
        description: t('feeds.supplementalFeedCreatedDesc'),
      });

      await refetchSupplementalFeeds();
    } catch (error: any) {
      if (error?.code === 'GOOGLE_REAUTH_REQUIRED') {
        sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
        toast.error(t('feeds.reconnectionRequired'), {
          description: (
            <span>
              {t('feeds.reconnectionRequiredDesc')}{' '}
              <a href="/settings?tab=accounts" className="text-primary underline">{t('googleAccounts.title')}</a>.
            </span>
          ),
        });
        setTimeout(() => navigate('/settings?tab=accounts'), 1500);
        return;
      }

      console.error('Error creating supplemental feed:', error);
      Sentry.captureException(error, { tags: { feature: 'feeds', action: 'create-supplemental' } });
      toast.error(t('error', { ns: 'common' }), {
        description: error.message || t('feeds.createSupplementalFailed'),
      });
    } finally {
      setCreatingForFeedId(null);
    }
  };

  const handleDeleteSupplementalFeed = async () => {
    if (!feedToDelete) return;

    setDeletingForFeedId(feedToDelete.feed_id);
    try {
      const res = await supabase.functions.invoke('delete-supplemental-feed', {
        body: {
          supplemental_feed_id: feedToDelete.id,
        }
      });

      // Check for token expiration in response data first (even with error status)
      if (res.data?.code === 'GOOGLE_REAUTH_REQUIRED') {
        sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
        toast.error(t('feeds.reconnectionRequired'), {
          description: (
            <span>
              {t('feeds.reconnectionRequiredDesc')}{' '}
              <a href="/settings?tab=accounts" className="text-primary underline">{t('googleAccounts.title')}</a>.
            </span>
          ),
        });
        setTimeout(() => navigate('/settings?tab=accounts'), 1500);
        return;
      }

      if (res.error) throw res.error;

      toast.success(t('feeds.feedDeleted'), {
        description: t('feeds.feedDeletedDesc'),
      });

      await refetchSupplementalFeeds();
      setDeleteDialogOpen(false);
      setFeedToDelete(null);
    } catch (error: any) {
      if (error?.code === 'GOOGLE_REAUTH_REQUIRED') {
        sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
        toast.error(t('feeds.reconnectionRequired'), {
          description: (
            <span>
              {t('feeds.reconnectionRequiredDesc')}{' '}
              <a href="/settings?tab=accounts" className="text-primary underline">{t('googleAccounts.title')}</a>.
            </span>
          ),
        });
        setTimeout(() => navigate('/settings?tab=accounts'), 1500);
        return;
      }

      console.error('Error deleting supplemental feed:', error);
      Sentry.captureException(error, { tags: { feature: 'feeds', action: 'delete-supplemental' } });
      toast.error(t('error', { ns: 'common' }), {
        description: error.message || t('feeds.deleteSupplementalFailed'),
      });
    } finally {
      setDeletingForFeedId(null);
    }
  };


  const hasFeeds = feeds && feeds.length > 0;

  return (
    <>
      <div className="space-y-6">
        <PageHeader title={t('feeds.title')} subtitle={t('feeds.subtitle')} as="h2" />

        {!selectedMerchantCenterId ? (
          <EmptyState
            icon={Store}
            title={t('feeds.noMerchantCenter')}
            description={t('feeds.noMerchantCenterDesc')}
            actionLabel={t('feeds.goToMerchantCenters')}
            onAction={() => navigate('/settings?tab=merchants')}
          />
        ) : loadingFeeds ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <FeedCardSkeleton key={i} />
            ))}
          </div>
        ) : !hasFeeds ? (
          <EmptyState
            icon={Rss}
            title={t('feeds.noFeedsFound')}
            description={t('feeds.noFeedsFoundDesc')}
            actionLabel={t('feeds.refreshMerchantCenters')}
            onAction={() => navigate('/settings?tab=merchants')}
          />
        ) : (
          <>
            <div className="space-y-4">
              {feeds.map((feed) => {
                const supplementalFeed = supplementalFeeds?.find(sf => sf.feed_id === feed.id) || null;
                const isLoadingThisFeed = creatingForFeedId === feed.id || deletingForFeedId === feed.id;
                
                return (
                  <Card key={feed.id} className="overflow-hidden">
                    <div 
                      className={`cursor-pointer transition-colors ${selectedFeedId === feed.id ? 'bg-accent/50' : 'hover:bg-accent/20'}`}
                      onClick={() => handleSelectFeed(feed.id)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Rss className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <CardTitle className="text-lg">{feed.display_name}</CardTitle>
                                <Badge variant="secondary">{t('feeds.primary')}</Badge>
                                {feed.product_count && feed.product_count > 0 && (
                                  <Badge variant="secondary">
                                    {t('feeds.productCount', { count: feed.product_count })}
                                  </Badge>
                                )}
                                {selectedFeedId === feed.id && <Badge variant="default">{t('feeds.selectedBadge')}</Badge>}
                              </div>
                              <CardDescription className="text-xs mt-1">
                                {t('feeds.feedId', { id: feed.feed_id })}
                              </CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">{t('feeds.country')}</span>{' '}
                            <Badge variant="outline">{feed.target_country}</Badge>
                          </div>
                          <div>
                            <span className="text-muted-foreground">{t('feeds.language')}</span>{' '}
                            <Badge variant="outline">{feed.content_language}</Badge>
                          </div>
                        </div>
                        
                        <SupplementalFeedInline
                          supplementalFeed={supplementalFeed}
                          merchantCenterId={merchantCenter?.merchant_center_id || ''}
                          loading={isLoadingThisFeed || loadingSupplementalFeeds}
                          onCreateFeed={() => handleCreateSupplementalFeed(feed.id)}
                          onDeleteFeed={() => {
                            if (supplementalFeed) {
                              setFeedToDelete(supplementalFeed);
                              setDeleteDialogOpen(true);
                            }
                          }}
                        />
                      </CardContent>
                    </div>
                    
                    {selectedFeedId === feed.id && (
                      <div className="border-t border-border px-6 pb-4">
                        <CustomInstructions
                          feedId={feed.id}
                          initialValue={feed.custom_prompt_instructions || null}
                        />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

          </>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('feeds.deleteSupplementalTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('feeds.deleteSupplementalDesc')}{' '}
              {t('feeds.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel', { ns: 'common' })}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSupplementalFeed}
              disabled={!!deletingForFeedId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingForFeedId ? t('actions.deleting', { ns: 'common' }) : t('feeds.deleteFeed')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Feeds;
